import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Search,
  Loader2,
  ShoppingCart,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import SupplierOrderFormDialog from "@/components/SupplierOrderFormDialog";

const STATUS_TONES = {
  draft: "bg-slate-100 text-slate-700 dark:bg-slate-800/60 dark:text-slate-300",
  sent: "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300",
  delivered: "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300",
  cancelled: "bg-rose-100 text-rose-800 dark:bg-rose-500/15 dark:text-rose-300",
};
const STATUS_LABELS = {
  draft: "Vázlat",
  sent: "Elküldve",
  delivered: "Leszállítva",
  cancelled: "Visszavonva",
};

const huFmt = new Intl.NumberFormat("hu-HU", { maximumFractionDigits: 0 });
const formatFt = (n) => (n == null ? "—" : `${huFmt.format(n)} Ft`);
const formatDate = (s) => {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString("hu-HU", {
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return s; }
};

const StatusBadge = ({ status }) => (
  <span
    data-testid={`order-status-${status}`}
    className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${STATUS_TONES[status] || ""}`}
  >
    {STATUS_LABELS[status] || status}
  </span>
);

const Th = ({ children, className = "" }) => (
  <th className={`whitespace-nowrap px-4 py-3 text-left text-[11px] font-medium uppercase tracking-[0.12em] ${className}`}>{children}</th>
);
const Td = ({ children, className = "", ...rest }) => (
  <td className={`whitespace-nowrap px-4 py-3 ${className}`} {...rest}>{children}</td>
);

const STATUS_FILTERS = [
  { v: "", label: "Mind" },
  { v: "draft", label: "Vázlat" },
  { v: "sent", label: "Elküldve" },
  { v: "delivered", label: "Leszállítva" },
  { v: "cancelled", label: "Visszavonva" },
];

export default function SupplierOrdersPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [openCreate, setOpenCreate] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = statusFilter ? { status: statusFilter } : {};
      const { data } = await api.get("/supplier-orders", { params });
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Betöltés sikertelen.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [statusFilter]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (o) =>
        (o.order_number || "").toLowerCase().includes(q) ||
        (o.supplier_name || "").toLowerCase().includes(q) ||
        (o.site_name || "").toLowerCase().includes(q) ||
        (o.company_name || "").toLowerCase().includes(q)
    );
  }, [items, search]);

  return (
    <div className="space-y-6 px-4 py-6 sm:px-6 lg:px-8" data-testid="supplier-orders-page">
      <header>
        <div className="flex items-center gap-1.5 text-muted-foreground/80">
          <ShoppingCart className="h-3.5 w-3.5" strokeWidth={2} />
          <span className="text-[10px] font-semibold uppercase tracking-[0.22em]">
            Beszerzés
          </span>
        </div>
        <h1 className="mt-1.5 text-2xl font-semibold tracking-tight">
          Beszállítói rendelések
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Új rendelés rögzítése, beszállítónak küldés emailben PDF mellékletekkel,
          szállítás visszaigazolása.
        </p>
      </header>

      <section className="overflow-hidden rounded-xl border border-border bg-[hsl(var(--sipx-elevated))]">
        <header className="flex flex-col gap-3 border-b border-border px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-1.5">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.v || "all"}
                type="button"
                onClick={() => setStatusFilter(f.v)}
                data-testid={`status-filter-${f.v || "all"}`}
                className={`h-8 rounded-full px-3 text-xs font-medium transition-colors ${
                  statusFilter === f.v
                    ? "bg-foreground text-background"
                    : "bg-muted/40 text-muted-foreground hover:bg-muted"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Keresés…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-9 w-56 pl-8"
                data-testid="order-search"
              />
            </div>
            <Button
              size="sm"
              onClick={() => setOpenCreate(true)}
              data-testid="new-order-button"
            >
              <Plus className="h-4 w-4" /> Új rendelés
            </Button>
          </div>
        </header>

        {loading ? (
          <div className="flex items-center justify-center px-6 py-14 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Betöltés…
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState onCreate={() => setOpenCreate(true)} hasFilter={!!statusFilter || !!search} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[920px] text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30 text-muted-foreground">
                  <Th>Rendelés</Th>
                  <Th>Beszállító</Th>
                  <Th>Telephely</Th>
                  <Th className="text-right">Tételek</Th>
                  <Th className="text-right">Bruttó</Th>
                  <Th>Státusz</Th>
                  <Th>Rögzítve</Th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((o) => (
                  <tr
                    key={o._id}
                    data-testid={`order-row-${o._id}`}
                    onClick={() => navigate(`/supplier-orders/${o._id}`)}
                    className="cursor-pointer border-b border-border last:border-0 transition-colors hover:bg-foreground/[0.025]"
                  >
                    <Td className="whitespace-nowrap">
                      <span className="font-mono text-[12px] font-medium">
                        {o.order_number || "—"}
                      </span>
                    </Td>
                    <Td>
                      <div className="font-medium">{o.supplier_name || "—"}</div>
                      {o.sent_to_email && (
                        <div className="text-[11px] text-muted-foreground">
                          {o.sent_to_email}
                        </div>
                      )}
                    </Td>
                    <Td className="text-muted-foreground">
                      <div>{o.site_name || "—"}</div>
                      {o.company_name && (
                        <div className="text-[11px]">{o.company_name}</div>
                      )}
                    </Td>
                    <Td className="text-right tabular-nums whitespace-nowrap">
                      {o.item_count || 0}
                    </Td>
                    <Td className="text-right tabular-nums font-medium whitespace-nowrap">
                      {formatFt(o.total_gross)}
                    </Td>
                    <Td className="whitespace-nowrap">
                      <StatusBadge status={o.status} />
                    </Td>
                    <Td className="text-muted-foreground text-xs whitespace-nowrap">
                      {formatDate(o.created_at)}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <SupplierOrderFormDialog
        open={openCreate}
        onOpenChange={setOpenCreate}
        onCreated={(order) => {
          setOpenCreate(false);
          navigate(`/supplier-orders/${order._id}`);
        }}
      />
    </div>
  );
}

function EmptyState({ onCreate, hasFilter }) {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
      <div className="grid h-10 w-10 place-items-center rounded-full border border-border bg-background">
        <ShoppingCart className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
      </div>
      <p className="text-sm font-medium">
        {hasFilter ? "Nincs találat" : "Nincs rögzített rendelés"}
      </p>
      <p className="text-xs text-muted-foreground">
        Rögzíts egy új beszállítói rendelést és küldd el e-mailben.
      </p>
      {!hasFilter && (
        <Button variant="outline" size="sm" className="mt-2 h-8" onClick={onCreate}>
          <Plus className="h-3.5 w-3.5" /> Új rendelés
        </Button>
      )}
    </div>
  );
}

export { StatusBadge, STATUS_LABELS, formatFt, formatDate };
